import { useEffect, useState } from "react";
import {
  AlertTriangle, Camera, CheckCircle2, FileText, Loader2, Printer, ShieldCheck, Upload,
} from "lucide-react";
import { guaranteeDeedText } from "@rental/shared";
import { guarantor as gApi } from "./lib/api.js";
import { sealForUpload } from "./lib/keyvault.js";
import { compressForUpload } from "./lib/images.js";
import { SignaturePad } from "./SignaturePad.jsx";
import { FilePick } from "./ui.jsx";
import { C, fontStack, inputStyle } from "./theme.js";

/* ============================================================================
   הדף שהערב רואה.

   הוא נפתח מקישור אחד בוואטסאפ, בלי חשבון ובלי קוד, ולכן הוא עומד בפני
   עצמו: לא נטען ממנו שום דבר מהאזור של הדייר או של בעל הנכס, והוא לא
   מנסה לזהות מי הגולש. כל מה שהוא יודע נמצא בטוקן שבכתובת.

   הזרימה היא מסך אחד ולא אשף: הערב פותח את ההודעה פעם אחת, ואם הוא צריך
   לחזור אליה מאוחר יותר הוא כבר לא חוזר. לכן החוזה, הפרטים, הצילום
   והחתימה נמצאים כולם לפניו, ושליחה אחת סוגרת את העניין.
   ============================================================================ */

export default function GuarantorPage({ token }) {
  const [state, setState] = useState({ phase: "loading" });

  const load = async () => {
    try {
      setState({ phase: "ready", data: await gApi.open(token) });
    } catch (err) {
      setState({ phase: "error", message: err.message });
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [token]);

  return (
    <div dir="rtl" className="min-h-screen w-full px-4 py-6"
      style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}>
      <div className="mx-auto w-full max-w-lg">
        {state.phase === "loading" && (
          <div className="flex justify-center py-20" style={{ color: C.muted }}>
            <Loader2 className="animate-spin" size={22} />
          </div>
        )}
        {state.phase === "error" && <Broken message={state.message} />}
        {state.phase === "ready" && (
          state.data.signed
            ? <Done data={state.data} />
            : <Form token={token} data={state.data} onSigned={load} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- מצבי קצה --- */

function Card({ children }) {
  return (
    <div className="rounded-2xl border p-5" style={{ background: C.card, borderColor: C.line }}>
      {children}
    </div>
  );
}

function Broken({ message }) {
  return (
    <Card>
      <div className="flex items-start gap-2">
        <AlertTriangle size={18} className="mt-0.5 shrink-0" style={{ color: C.ochre }} />
        <div>
          <h1 className="text-lg font-bold mb-1">הקישור לא נפתח</h1>
          <p className="text-sm" style={{ color: C.muted }}>{message}</p>
          <p className="text-sm mt-2" style={{ color: C.muted }}>
            בקשו מהשוכר לשלוח קישור חדש.
          </p>
        </div>
      </div>
    </Card>
  );
}

function Done({ data }) {
  const deed = data.finalDeed;
  return (
    <>
      <Card>
        <div className="text-center py-4">
          <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: C.paid }} />
          <h1 className="text-xl font-bold mb-1">כתב הערבות נחתם</h1>
          <p className="text-sm" style={{ color: C.muted }}>
            תודה. החתימה נשמרה ונשלחה ל{data.owner || "בעל הנכס"}
            {data.apartment ? ` עבור ${data.apartment}` : ""}.
          </p>
          <p className="text-xs mt-4" style={{ color: C.muted }}>
            {deed ? "המסמך החתום מוצג למטה — מומלץ לשמור עותק." : "אפשר לסגור את הדף. אין צורך לשמור את הקישור."}
          </p>
        </div>
      </Card>

      {/* המסמך הסופי כפי שנשמר: הנוסח המדויק, החתימה, חותמת הזמן, מזהה
          המסמך וטביעת האצבע — מה שמודפס הוא מה שנשמר, אות באות. */}
      {deed && (
        <>
          <div className="h-3" />
          <Card>
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="text-sm" style={{ fontWeight: 600 }}>המסמך החתום</h2>
              <button onClick={() => window.print()}
                className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
                style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                <Printer size={13} /> הדפסה / שמירה כ-PDF
              </button>
            </div>
            <div className="deed-print rounded-lg border p-4" style={{ borderColor: C.line, background: "#fff" }}>
              <div style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.8 }}>{deed.text}</div>
              <div className="mt-3 text-[12px]" style={{ color: C.muted }}>{deed.consent}</div>
              {data.signature && (
                <div className="mt-3">
                  <div className="text-[11px]" style={{ color: C.muted }}>חתימת הערב/ה:</div>
                  <img src={data.signature} alt="חתימה" style={{ height: 64 }} />
                </div>
              )}
              <div className="text-[11px] mt-4 pt-2" style={{ borderTop: `1px solid ${C.line}`, color: C.muted, lineHeight: 1.8 }}>
                נחתם דיגיטלית: {new Date(deed.signedAt).toLocaleString("he-IL")}
                {deed.otp?.verified ? " · זהות הטלפון אומתה בקוד SMS" : ""}
                <br />מזהה מסמך: <span dir="ltr">{deed.id}</span>
                <br />טביעת אצבע (SHA-256): <span dir="ltr" style={{ fontSize: 9, wordBreak: "break-all" }}>{deed.sha256}</span>
              </div>
            </div>
          </Card>
          <style>{`@media print {
            body * { visibility: hidden !important; }
            .deed-print, .deed-print * { visibility: visible !important; }
            .deed-print { position: absolute !important; inset: 0 !important; padding: 24px !important; border: none !important; }
          }`}</style>
        </>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- הטופס --- */

function Form({ token, data, onSigned }) {
  const [f, setF] = useState({
    name: data.guarantor.name || "",
    idNumber: data.guarantor.idNumber || "",
    phone: data.guarantor.phone || "",
    address: data.guarantor.address || "",
  });
  const [idPhotoId, setIdPhotoId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showContract, setShowContract] = useState(false);
  const [idNote, setIdNote] = useState("");
  const [err, setErr] = useState("");

  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErr(""); setIdNote(""); };

  /* הצילום מוצפן למפתח הציבורי של בעל הנכס, בדיוק כמו זה של הדייר: הערב
     מוסר תעודת זהות לאדם אחד, ולא לכל מי שיש לו גישה לאחסון. בחשבון שעוד
     לא הגדיר הצפנה אין מפתח — אז מעלים גלוי, כי ערב שנתקע גרוע יותר. */
  const attachId = async (file) => {
    setUploading(true);
    setErr(""); setIdNote("");
    try {
      const dataUrl = await compressForUpload(file);
      /* זיהוי המספר רץ במקביל להעלאה — על העותק שלפני ההצפנה. כישלון
         זיהוי אינו כישלון העלאה: פשוט מקלידים את המספר ידנית. */
      const ocr = gApi.readId(token, dataUrl).catch(() => null);
      const sealed = data.ownerPublicKey
        ? await sealForUpload(dataUrl, data.ownerPublicKey)
        : null;
      setIdPhotoId(await gApi.uploadId(token, dataUrl, sealed));
      const out = await ocr;
      if (out?.idNumber) {
        setF((cur) => (cur.idNumber.trim() ? cur : { ...cur, idNumber: out.idNumber }));
        setIdNote(out.confidence === "low"
          ? `זיהינו מהצילום ${out.idNumber}, אבל לא בוודאות — בדקו את המספר.`
          : `מספר הזהות ${out.idNumber} זוהה מהצילום. בדקו שהוא נכון.`);
      }
    } catch (ex) {
      setErr(ex.message || "העלאת הצילום נכשלה.");
    }
    setUploading(false);
  };

  /* אישור מפורש + אימות SMS — שכבות הזיהוי שלפני החתימה. */
  const [agreed, setAgreed] = useState(false);
  const [otpOk, setOtpOk] = useState(!!data.otp?.verified);
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpErr, setOtpErr] = useState("");

  const sendOtp = async () => {
    setOtpBusy(true); setOtpErr("");
    try {
      // הטלפון שנרשם מראש הוא העוגן; בלעדיו נשלח למה שהוקלד בטופס.
      const r = await gApi.otpSend(token, data.otp?.phone ? null : f.phone.trim());
      if (r.sent) setOtpSentTo(r.phone);
      else setOtpErr(r.reason === "not_configured" ? "" : "שליחת הקוד נכשלה. נסו שוב.");
    } catch (ex) { setOtpErr(ex.message); }
    setOtpBusy(false);
  };
  const verifyOtp = async () => {
    setOtpBusy(true); setOtpErr("");
    try {
      await gApi.otpVerify(token, otpCode);
      setOtpOk(true);
    } catch (ex) { setOtpErr(ex.message); }
    setOtpBusy(false);
  };

  /* הנוסח המלא, חי: מתעדכן תוך כדי מילוי הפרטים — אותה פונקציה בדיוק
     בונה בשרת את הנוסח שנשמר ברגע החתימה, ולכן מה שמוצג הוא מה שנחתם. */
  const deedText = guaranteeDeedText({
    apt: { name: data.apartment, tenant: data.tenant },
    guarantor: f,
    contract: data.contract,
    settings: data.deedContext || { ownerName: data.owner },
  });
  const consentText = data.consentText
    || "אני מאשר/ת שקראתי את הסכם השכירות ואת כתב הערבות, הבנתי את התחייבויותיי כערב/ה, ואני חותם/ת מרצוני החופשי.";

  /* השגיאה נתפסת כאן ולא נזרקת הלאה כדי שתוצג בניסוח של השרת ("הקישור
     אינו תקף…") ולא כ"השמירה נכשלה" של משטח החתימה. */
  const submit = async (signature) => {
    setErr("");
    try {
      await gApi.submit(token, { ...f, signature, idPhotoId, consent: agreed });
      await onSigned();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const missing = !f.name.trim()
    ? "צריך למלא שם מלא"
    : !f.idNumber.trim()
      ? "צריך למלא מספר תעודת זהות"
      : !agreed
        ? "צריך לסמן את אישור הקריאה לפני החתימה"
        : data.otp?.required && !otpOk
          ? "צריך לאמת את הטלפון בקוד ה-SMS לפני החתימה"
          : null;

  return (
    <>
      <div className="text-xs tracking-widest mb-2" style={{ color: C.ochre }}>
        כתב ערבות
      </div>

      <Card>
        <h1 className="disp text-xl font-bold mb-1">
          חתימה כערב
        </h1>
        <p className="text-sm" style={{ color: C.muted }}>
          {data.tenant || "השוכר"} ביקש שתחתמו כערב על הסכם השכירות
          {data.apartment ? ` ב${data.apartment}` : ""}
          {data.owner ? `, מול ${data.owner}` : ""}.
        </p>

        {data.contract && (
          <div className="mt-3 rounded-lg p-3 text-sm" style={{ background: C.surface }}>
            <div className="flex items-center justify-between gap-2">
              <span style={{ color: C.muted }}>תקופת השכירות</span>
              <span dir="ltr">{heShort(data.contract.from)} – {heShort(data.contract.to)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 mt-1">
              <span style={{ color: C.muted }}>דמי שכירות</span>
              <span>₪{Number(data.contract.rent).toLocaleString("he-IL")} לחודש</span>
            </div>
          </div>
        )}

        {/* קריאת החוזה לפני החתימה היא כל העניין. הוא סגור כברירת מחדל כי
            הוא ארוך, אבל הכפתור יושב לפני הטופס ולא אחריו. */}
        {data.contract?.text && (
          <>
            <button onClick={() => setShowContract(!showContract)}
              className="mt-3 w-full rounded-xl py-2.5 text-sm flex items-center justify-center gap-2"
              style={{ border: `1px solid ${C.line}`, color: C.teal }}>
              <FileText size={15} /> {showContract ? "סגירת החוזה" : "קריאת החוזה שעליו אתם ערבים"}
            </button>
            {showContract && (
              <div className="mt-2 rounded-lg border p-3"
                style={{ borderColor: C.line, maxHeight: 380, overflowY: "auto", background: "#fff" }}>
                <div style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.7 }}>
                  {data.contract.text}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <div className="h-3" />

      <Card>
        <h2 className="text-sm mb-3" style={{ fontWeight: 600 }}>הפרטים שלכם</h2>

        <label className="text-[12px]" style={{ color: C.muted }}>שם מלא</label>
        <input value={f.name} onChange={set("name")} autoComplete="name"
          style={{ ...inputStyle, marginBottom: 10 }} />

        <label className="text-[12px]" style={{ color: C.muted }}>תעודת זהות</label>
        <input value={f.idNumber} onChange={set("idNumber")} inputMode="numeric"
          dir="ltr" style={{ ...inputStyle, marginBottom: idNote ? 4 : 10, textAlign: "right" }} />
        {idNote && (
          <p className="text-[11px] mb-2" style={{ color: C.teal }}>{idNote}</p>
        )}

        <label className="text-[12px]" style={{ color: C.muted }}>טלפון</label>
        <input value={f.phone} onChange={set("phone")} inputMode="tel" autoComplete="tel"
          dir="ltr" style={{ ...inputStyle, marginBottom: 10, textAlign: "right" }} />

        <label className="text-[12px]" style={{ color: C.muted }}>כתובת</label>
        <input value={f.address} onChange={set("address")} autoComplete="street-address"
          style={inputStyle} />

        <div className="mt-4">
          <div className="text-[12px] mb-1.5" style={{ color: C.muted }}>
            צילום תעודת זהות
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <FilePick capture title="צילום תעודת זהות" disabled={uploading}
              onPick={attachId}
              className="text-sm rounded-lg px-3 py-2 gap-1.5"
              style={{ border: `1px solid ${C.line}`, color: C.teal }}>
              <Camera size={15} /> {uploading ? "מעלה…" : "צילום"}
            </FilePick>
            <FilePick title="העלאה מהגלריה" disabled={uploading} onPick={attachId}
              className="text-sm rounded-lg px-3 py-2 gap-1.5"
              style={{ border: `1px solid ${C.line}`, color: C.muted }}>
              <Upload size={15} /> העלאה
            </FilePick>
            {idPhotoId && (
              <span className="text-[12px] flex items-center gap-1" style={{ color: C.paid }}>
                <CheckCircle2 size={13} /> הצילום נשמר
              </span>
            )}
          </div>
          {/* אמירה מפורשת. הערב מוסר מסמך מזהה לאדם שהוא לא בהכרח מכיר. */}
          <p className="text-[11px] mt-1.5" style={{ color: C.muted }}>
            {data.ownerPublicKey
              ? "הצילום מוצפן במכשיר שלכם, ורק בעל הנכס יכול לפתוח אותו."
              : "הצילום נשמר אצל בעל הנכס."}
          </p>
        </div>
      </Card>

      <div className="h-3" />

      {/* הנוסח המלא — לא משפט מעל משטח החתימה אלא המסמך עצמו, מתעדכן
          חי לפי הפרטים שמולאו. הוא שנשמר כלשונו עם החתימה. */}
      <Card>
        <h2 className="text-sm mb-1" style={{ fontWeight: 600 }}>כתב הערבות — הנוסח המלא</h2>
        <p className="text-[11px] mb-2" style={{ color: C.muted }}>
          זה הנוסח המדויק שעליו תחתמו. הוא מתעדכן לפי הפרטים שמילאתם, ויישמר
          כלשונו יחד עם החתימה, חותמת הזמן ומזהה המסמך.
        </p>
        <div className="rounded-lg border p-3" style={{ borderColor: C.line, background: "#fff", maxHeight: 320, overflowY: "auto" }}>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.8 }}>{deedText}</div>
        </div>
      </Card>

      <div className="h-3" />

      <Card>
        <h2 className="text-sm mb-3" style={{ fontWeight: 600 }}>חתימה על כתב הערבות</h2>

        {/* אימות SMS — כשמוגדר ערוץ שליחה. עם טלפון שנרשם מראש זו חובה. */}
        {data.otp?.available && !otpOk && (data.otp?.required || f.phone.trim()) && (
          <div className="rounded-lg p-3 mb-3" style={{ background: C.surface }}>
            <div className="text-[12px] mb-1.5 flex items-center gap-1.5" style={{ fontWeight: 600 }}>
              <ShieldCheck size={14} style={{ color: C.teal }} /> אימות הטלפון בקוד SMS
            </div>
            {otpSentTo ? (
              <>
                <div className="text-[12px] mb-1.5" style={{ color: C.muted }}>שלחנו קוד ל-{otpSentTo}. הקלידו אותו:</div>
                <div className="flex flex-wrap items-center gap-2">
                  <input inputMode="numeric" dir="ltr" value={otpCode}
                    onChange={(e) => { setOtpCode(e.target.value); setOtpErr(""); }}
                    placeholder="6 ספרות" style={{ ...inputStyle, maxWidth: 130, textAlign: "center" }} />
                  <button onClick={verifyOtp} disabled={otpBusy || otpCode.replace(/\D/g, "").length < 4}
                    className="text-sm rounded-lg px-3 py-2" style={{ background: C.teal, color: "#fff", opacity: otpBusy ? 0.6 : 1 }}>
                    אימות
                  </button>
                  <button onClick={sendOtp} disabled={otpBusy} className="text-[11px]" style={{ color: C.muted }}>
                    שליחה חוזרת
                  </button>
                </div>
              </>
            ) : (
              <button onClick={sendOtp} disabled={otpBusy}
                className="text-sm rounded-lg px-3 py-2 flex items-center gap-1.5"
                style={{ border: `1px solid ${C.line}`, color: C.teal, opacity: otpBusy ? 0.6 : 1 }}>
                <ShieldCheck size={14} /> שליחת קוד אימות ל-{data.otp?.phone || "הטלפון שמילאתם"}
              </button>
            )}
            {otpErr && <div className="text-[11px] mt-1.5" style={{ color: "#B3261E" }}>{otpErr}</div>}
          </div>
        )}
        {otpOk && data.otp?.available && (
          <div className="rounded-lg p-2.5 mb-3 text-[12px] flex items-center gap-1.5" style={{ background: "#EAF3EE", color: C.paid }}>
            <CheckCircle2 size={14} /> הטלפון אומת בקוד SMS
          </div>
        )}

        {/* האישור המפורש — נאכף גם בשרת: אין חתימה בלי הצהרת קריאה. */}
        <label className="flex items-start gap-2 text-[12px] mb-3 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={agreed}
            onChange={(e) => { setAgreed(e.target.checked); setErr(""); }} />
          <span>{consentText}</span>
        </label>

        <SignaturePad
          saveLabel="חתימה ושליחה"
          guard={() => missing}
          onSave={submit}
        />

        {err && (
          <div className="mt-3 rounded-lg p-2.5 text-sm flex items-start gap-2"
            style={{ background: "#FBF2E3", color: "#6B4E12" }}>
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}
      </Card>

      <p className="text-[11px] text-center mt-4" style={{ color: C.muted }}>
        הקישור אישי ובתוקף עד {heShort(data.expiresAt)}.
      </p>
    </>
  );
}

/* התאריכים מגיעים מהשרת כ-ISO. אין כאן את עזרי התאריך של המסמך, ואין
   סיבה לגרור אותם לדף שעומד בפני עצמו. */
function heShort(value) {
  if (!value) return "—";
  const d = new Date(value);
  // בלי 2-digit: כל שאר המערכת מציגה 1.2.2026 ולא 01.02.2026, ודף
  // הערב הוא היחיד שנראה אחרת — באותו חוזה שהוא חותם עליו.
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("he-IL");
}
